<template>
  <LoginCard>
    <h1 :class="$style.header">
      User Login
    </h1>
    <LoginAlert
      v-model="loadingSuccessMsg"
      type="success"
    />
    <LoginAlert
      v-model="loadingErrorMsg"
    />
    <LoginAlert
      centered
      v-model="errorMessage"
    >
      <div v-if="errorMessage">
        {{ errorMessage }}
        <div>
          <span>Are you trying to </span>
          <a
            :class="$style.swapClientTypeLink"
            @click="goToAdminLogin"
          >
            log in as an Administrator?
          </a>
        </div>
      </div>
    </LoginAlert>
    <Credentials
      :action="authURL"
      :autofocus="autofocus"
      :form="form"
      :isAdmin="false"
      :isLoading="isLoading"
      :ref="formRef"
      :submit="submit"
      submitButtonText="User Login"
      :updateAutofocus="updateAutofocus"
      v-bind="$route.query"
    />
    <div :class="$style.resetPassword">
      <span
        :class="$style.resetPasswordLink"
        @click="redirect(resetPasswordRoute)"
      >
        Reset User Password
      </span>
    </div>
  </LoginCard>
</template>

<script>
import { LoginConsts } from 'client/util/Constants';
import { UserRoutes } from 'client/routes/RouteGenerator';
import Credentials from 'client/components/forms/Credentials.vue';
import LoginCard from 'client/components/LoginCard.vue';
import api from 'client/api/UserLoginApi';
import autoFillCredentialsFix from 'client/components/mixins/autoFillCredentialsFix';
import loginResponseHandlers, { loginErrorHandler } from 'client/containers/Login/LoginResponseHandlers';
import loginUtil from 'client/components/mixins/loginUtil';

const {
  authURLs,
  clientType,
  sso,
} = LoginConsts;
const { formRef } = LoginConsts.formElements;
const { dashboard } = UserRoutes;
const { user } = clientType;

export default {
  name: 'UserLoginForm',

  mixins: [
    autoFillCredentialsFix,
    loginUtil,
    loginResponseHandlers,
  ],

  components: {
    Credentials,
    LoginCard,
  },

  props: {
    redirectTo: {
      type: String,
      default: '',
    },
    // loadingErrorMsg differs from errorMessage in that error is passed in from the parent,
    // and errorMessage is an error from component interaction.
    loadingErrorMsg: {
      type: String,
      default: undefined,
    },
    loadingSuccessMsg: {
      type: String,
      default: undefined,
    },
    form: {
      type: Object,
      default: () => ({
        email: '',
        password: '',
      }),
    },
  },

  data() {
    return {
      authURL: authURLs[user],
    };
  },

  mounted() {
    const message = this.$route.params.error;

    if (message) {
      this.setErrorMessage(message);
    }
  },

  computed: {
    formRef: () => formRef,
    resetPasswordRoute: () => UserRoutes.resetPassword,
  },

  methods: {
    goToAdminLogin() {
      this.vueRouteChange('Admin Login', { form: this.form });
    },

    getPayload() {
      const payload = this.getCredentialsPayload();
      return this.redirectTo ? { ...payload, context: sso, redirectTo: this.redirectTo } : payload;
    },

    async request() {
      const {
        loginSuccessHandler, mfaRedirectHandler, setErrorMessage,
      } = this;

      try {
        await api.login(this.getPayload())
          .finally(() => {
            this.isLoading = false;
          });
      } catch (e) {
        const result = loginErrorHandler(e);

        if (result instanceof Error) {
          return setErrorMessage(result.message);
        }
        /*
        this will get overwritten by the redirect() method
        in loginUtil if redirectTo is set in session storage
        */
        return mfaRedirectHandler(result, { redirectTo: dashboard });
      }
      /*
      this will get overwritten by the redirect() method
      in loginUtil if redirectTo is set in session storage
      */
      return loginSuccessHandler(dashboard);
    },
  },
};
</script>

<style module>
@import '../../css/login-components.css';

.container {
  padding: 1rem;
}
</style>
